import React, { useState, useRef, Suspense } from 'react';
import { TextField, Box, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import {LockPersonOutlined} from "@mui/icons-material";
import {backgroundColor, colorText, monacoTheme} from "../../App/theme.js";
import {isContentHTML, isMobile} from "../../App/utils.js";
import { HTMLToIframe } from "../HTMLToIframe.js";

const LazyEditor = React.lazy(() => import('@monaco-editor/react'));

export const FieldBody = ({body, setItemBody, attr, disabled}) => {
    const isFullscreen = new URLSearchParams(window.location.search).get('fullscreen') !== null;
    const isHTML = isContentHTML(body);
    const iframeRef = useRef(null);
    const [contentType, setContentType] = useState('text');
    const [previewMode, setPreviewMode] = useState(isFullscreen);

    const isJSON = contentType === 'json';
    const fontSize = isJSON ? 12 : undefined;

    const labelBody = (<div style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', fontSize }}>
        <LockPersonOutlined style={{ marginRight: 8, fontSize: isJSON ? 18 : 24 }} />
        <span style={{ position: 'relative', top: 0, color: colorText }}>
            {attr?.attrName ? attr.attrName : 'Body'} {isJSON ? '*' : ''}
        </span>
    </div>);

    return (
        <Box sx={{width: '100%'}}>
            {!disabled && <Box mt={0} display="flex" justifyContent={isMobile ? "center" : "flex-end"}>
                <FormControl component="fieldset">
                    <RadioGroup
                        row
                        aria-label="content-type"
                        name="content-type"
                        value={isHTML ? ( previewMode ? 'preview' : 'html') : contentType}
                        onChange={(e) => setContentType(e.target.value)}
                    >
                        {!isHTML && <>
                            <FormControlLabel
                                value="text"
                                control={<Radio sx={{ transform: 'scale(0.8)' }} />}
                                label="TEXT"
                                sx={{ color: colorText, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value="json"
                                control={<Radio sx={{ transform: 'scale(0.8)' }} />}
                                label="JSON"
                                sx={{ color: colorText, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                        </>}

                        {isHTML && <>
                            <FormControlLabel
                                value="html"
                                control={<Radio sx={{ transform: 'scale(0.8)' }} />}
                                label="HTML"
                                onClick={() => setPreviewMode(false)}
                                sx={{ color: colorText, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />

                            <FormControlLabel
                                value="preview"
                                control={<Radio sx={{ transform: 'scale(0.8)' }} />}
                                label="Preview"
                                onClick={() => setPreviewMode(true)}
                                sx={{ color: colorText, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                        </>}
                    </RadioGroup>
                </FormControl>
            </Box>}

            <Box mt={1}>
                {contentType === 'text' && !isHTML && (
                    <TextField
                        disabled={!!disabled}
                        autoComplete="off"
                        label={labelBody}
                        value={body || ''}
                        onChange={(e) => setItemBody && setItemBody(e.target.value)}
                        fullWidth
                        multiline
                        minRows={1}
                        variant="outlined"
                        placeholder={ attr?.placeholder ? attr?.placeholder : "Enter plain text content here"}
                    />
                )}

                {contentType === 'json' && !isHTML && (
                    <Suspense fallback={<div>Loading...</div>}>
                        <div style={{marginLeft: '-28px'}}>
                            <div style={{position: 'absolute', zIndex: 10, marginTop: '-8px', marginLeft: '40px'}}>
                                {labelBody}
                            </div>
                        <LazyEditor
                            options={{
                                padding: {
                                    top: 20
                                },
                                lineNumbers: 'off',
                                scrollbar: {
                                    scrollByItem: true,
                                    readOnly: false,
                                },
                                minimap: {
                                    enabled: false
                                }
                            }}
                            height="217px"
                            defaultLanguage="json"
                            value={body || ''}
                            onChange={(v) => setItemBody && setItemBody(v)}
                            language="json"
                            theme={`custom-${monacoTheme}`}
                            beforeMount={(monaco) => {
                                // Define a new theme based on 'vs-dark' and override the background color
                                monaco.editor.defineTheme(`custom-${monacoTheme}`, {
                                    base: monacoTheme,
                                    inherit: true,
                                    rules: [],
                                    colors: {
                                        'editor.background': backgroundColor,
                                    }
                                });

                                monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                                    validate: true,
                                    schemas: [{
                                        fileMatch: ["*"], // associate with all models
                                        schema: {
                                            type: "object",
                                            properties: {
                                                name: {
                                                    type: "string",
                                                    description: "Name of the item",
                                                    example: "Sample Item",
                                                    sortText: 1
                                                },
                                                description: {
                                                    type: "string",
                                                    description: "Description of the item",
                                                    example: "This is a sample item used for demonstration purposes.",
                                                    sortText: 2
                                                }
                                            },
                                        }
                                    }]
                                });
                            }}
                        />
                        </div>
                    </Suspense>
                )}

                {isHTML && previewMode && HTMLToIframe(body, iframeRef, isFullscreen)}

                {isHTML && !previewMode && (
                    <Suspense fallback={<div>Loading...</div>}>
                        <LazyEditor
                            options={{
                                scrollbar: {
                                    scrollByItem: true,
                                    readOnly: false,
                                }
                            }}
                            height="90vh"
                            defaultLanguage="html"
                            value={body || ''}
                            onChange={(v) => setItemBody && setItemBody(v)}
                            language="html"
                            theme={`custom-${monacoTheme}`}
                            beforeMount={(monaco) => {
                                monaco.editor.defineTheme(`custom-${monacoTheme}`, {
                                    base: monacoTheme,
                                    inherit: true,
                                    rules: [],
                                    colors: {
                                        'editor.background': backgroundColor,
                                    }
                                })
                            }}
                        />
                    </Suspense>
                )}
            </Box>
        </Box>
    )
}